import { useEffect, useState, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import useSelectedDomain from "../service/Hooks/useSelectedDomain";
import { getUserRole } from "../service/api/AuthRoleService";
import { useSecureDomain } from "../service/Hooks/useSecureDomain";

export function Sidebar() {
  const { t } = useTranslation('sidebar', 'common');
  const { getSecureDomainPath } = useSecureDomain();

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector(".header");
      if (!header) return;
      const headerHeight = header.offsetHeight;
      const body = document.body;
      if (
        body.getAttribute("data-layout") === "horizontal" &&
        body.getAttribute("data-header-position") === "static" &&
        body.getAttribute("data-sidebar-position") === "fixed"
      ) {
        const dlabnav = document.querySelector(".dlabnav");
        if (window.scrollY >= headerHeight) {
          dlabnav && dlabnav.classList.add("fixed");
        } else {
          dlabnav && dlabnav.classList.remove("fixed");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const { user } = useUser();
  const location = useLocation();
  const [role, setRole] = useState(() => {
    const storedRole = sessionStorage.getItem("userRole");
    return storedRole || null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const selectedDomain = useSelectedDomain();
  const [hovered, setHovered] = useState(null);

  const fetchUserRole = async (isRetry = false) => {
    if (!user) return;
    
    if (isRetry) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const fetchedRole = await getUserRole(user, selectedDomain);
      setRole(fetchedRole);
    } catch (error) {
      if (isRetry || !role) {
        setError(error.message);
      }
      
      if (!role) {
        setRole("unknown");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserRole();
  }, [user, selectedDomain]);

  const menuItems = useMemo(() => {
    if (!role || role === "unknown") return [];

    const menus = {
      admin: [
        { path: "/Trade-Dashboard-admin", icon: "dashboard", label: t('admin.dashboard') },
        { path: "/validation-admin", icon: "verified_user", label: t('admin.validations') }
      ],
      manager: [
        { path: "/Trade-Dashboard-manager", icon: "dashboard", label: t('manager.dashboard') },
        { 
          path: selectedDomain ? getSecureDomainPath("/Trade-Customer") : "", 
          icon: "group", 
          label: t('manager.customers')
        },
        { path: "/Trade-Securities", icon: "account_balance", label: t('manager.securities') },
        { path: "/Trade-Ordre", icon: "inventory", label: t('manager.orders') },
        { path: "/Trade-Transactions", icon: "compare_arrows", label: t('manager.transactions') },
        { path: "/Trade-Ost", icon: "event", label: t('manager.ost') },
        { path: "/Trade_Facturation", icon: "receipt", label: t('manager.billing') },
      ],
      validateur: [
        { path: "/Trade-Dashboard-manager", icon: "dashboard", label: t('validateur.dashboard') },
        { 
          path: selectedDomain ? getSecureDomainPath("/Trade-Customer") : "", 
          icon: "group", 
          label: t('validateur.customers')
        },
        { path: "/Trade-Securities", icon: "account_balance", label: t('validateur.securities') },
        { path: "/Trade-Ordre", icon: "inventory", label: t('validateur.orders') },
        { path: "/Trade-Transactions", icon: "compare_arrows", label: t('validateur.transactions') },
        { path: "/Trade-Ost", icon: "event", label: t('validateur.ost') },
        { path: "/Trade_Facturation", icon: "receipt", label: t('validateur.billing') },
        { path: "/Trade-Dashboard-validateur", icon: "verified_user", label: t('validateur.validations') }
      ],
      client: [
        { path: "Trade-Dashboard-client", icon: "dashboard", label: t('client.dashboard') },
        { path: "/page/app/customer/transaction_customer_trade_vault", icon: "compare_arrows", label: t('client.transactions') },
        { path: "/page/app/customer/order_customer_trade_vault", icon: "inventory", label: t('client.orders') },
        { path: "/page/app/customer/securities_lists_customer_trade_vault", icon: "account_balance", label: t('client.securities') },
      ],
      unknown: []
    };

    if (role === "admin") return menus.admin;
    if (role === "admin+manager") return [...menus.admin, ...menus.manager];
    if (role === "manager") return menus.manager;
    if (role === "validateur") return menus.validateur; 
    if (role === "client") return menus.client;
    if (role === "unknown") return menus.unknown;

    return [];
  }, [role, selectedDomain, t]);

  // Styles intégrés
  const sidebarStyles = ` 
    .sidebar-modern {
      position: relative;
      padding-top: 30px; 
    }
    
    .sidebar-modern .dlabnav {
      background: linear-gradient(135deg, #1a1f36 0%, #2d3748 100%);
      border-right: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
    }
    
    .sidebar-modern .metismenu {
      padding: 1rem 0.5rem;
      margin-top: 15px;
    }
    
    .sidebar-modern .metismenu li {
      margin: 0.25rem 0;
      position: relative;
    }
    
    .sidebar-modern .metismenu li a {
      display: flex;
      align-items: center;
      padding: 0.875rem 1rem;
      color: #cbd5e0;
      text-decoration: none;
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
      border: 1px solid transparent;
      background: rgba(255, 255, 255, 0.02);
    }
    
    .sidebar-modern .metismenu li a::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 0;
      background: linear-gradient(135deg, #1a1f36 0%, #2d3748 100%);
      border-radius: 0 4px 4px 0;
      transition: height 0.3s ease;
    }
    
    .sidebar-modern .metismenu li a:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
      border-color: rgba(255, 255, 255, 0.1);
      transform: translateX(4px);
    }
    
    .sidebar-modern .metismenu li a:hover::before {
      height: 60%;
    }
    
    .sidebar-modern .metismenu li a.active-link {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
      color: #ffffff;
      border-color: rgba(102, 126, 234, 0.3);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
      font-weight: 600;
    }
    
    .sidebar-modern .metismenu li a.active-link::before {
      height: 80%;
      background: linear-gradient(135deg, #1a1f36 0%, #2d3748 100%);
    }
    
    .sidebar-modern .material-icons {
      font-size: 1.25rem;
      margin-right: 0.75rem;
      transition: all 0.3s ease;
      background: linear-gradient(135deg, #667eea, #764ba2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .sidebar-modern .metismenu li a:hover .material-icons {
      transform: scale(1.1);
    }
    
    .sidebar-modern .metismenu li a.active-link .material-icons {
      background: linear-gradient(135deg, #ffffff, #e2e8f0);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .sidebar-modern .nav-text {
      font-size: 0.875rem;
      font-weight: 500;
      transition: all 0.3s ease;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .sidebar-modern .loading-indicator {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
      border-bottom: 1px solid rgba(102, 126, 234, 0.2);
    }
    
    .sidebar-modern .loading-indicator .material-icons {
      background: linear-gradient(135deg, #667eea, #764ba2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: spin 1.5s linear infinite;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .sidebar-modern .error-state {
      background: linear-gradient(135deg, rgba(254, 178, 178, 0.1) 0%, rgba(229, 62, 62, 0.1) 100%);
      border: 1px solid rgba(229, 62, 62, 0.2);
    }
    
    .sidebar-modern .error-state .material-icons {
      background: linear-gradient(135deg, #fc8181, #e53e3e);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    .sidebar-modern .metismenu li {
      animation: slideIn 0.4s ease-out forwards;
    }
    
    .sidebar-modern .metismenu li:nth-child(1) { animation-delay: 0.1s; }
    .sidebar-modern .metismenu li:nth-child(2) { animation-delay: 0.15s; }
    .sidebar-modern .metismenu li:nth-child(3) { animation-delay: 0.2s; }
    .sidebar-modern .metismenu li:nth-child(4) { animation-delay: 0.25s; }
    .sidebar-modern .metismenu li:nth-child(5) { animation-delay: 0.3s; }
    .sidebar-modern .metismenu li:nth-child(6) { animation-delay: 0.35s; }
    .sidebar-modern .metismenu li:nth-child(7) { animation-delay: 0.4s; }
    
    @media (max-width: 1024px) {
      .sidebar-modern .metismenu li a {
        padding: 0.75rem 0.875rem;
      }
      
      .sidebar-modern .nav-text {
        font-size: 0.8rem;
      }
      
      .sidebar-modern .material-icons {
        font-size: 1.125rem;
        margin-right: 0.625rem;
      }
    }
    
    @media (max-width: 768px) {
      .sidebar-modern .metismenu {
        padding: 0.75rem 0.25rem;
      }
      
      .sidebar-modern .metismenu li a {
        padding: 0.625rem 0.75rem;
        border-radius: 8px;
      }
      
      .sidebar-modern .nav-text {
        font-size: 0.75rem;
      }
    }

    @media (min-width: 1200px) {
      .sidebar-modern .metismenu {
        padding-top: 25px;
      }
    }
    
    @media (min-width: 1400px) {
      .sidebar-modern .metismenu {
        padding-top: 30px;
      }
    }
    
    @media (min-width: 1600px) {
      .sidebar-modern .metismenu {
        padding-top: 35px;
      }
    }
    
    .sidebar-modern .dlabnav-scroll::-webkit-scrollbar {
      width: 4px;
    }
    
    .sidebar-modern .dlabnav-scroll::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
      border-radius: 2px;
    }
    
    .sidebar-modern .dlabnav-scroll::-webkit-scrollbar-thumb {
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 2px;
    }
    
    .sidebar-modern .dlabnav-scroll::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(135deg, #764ba2, #667eea);
    }
  `;

  // État de chargement initial
  if (loading && !role) {
    return (
      <div className="sidebar-modern">
        <style>{sidebarStyles}</style>
        <div className="dlabnav">
          <div className="dlabnav-scroll">
            <div style={{ 
              padding: "2rem", 
              textAlign: "center", 
              color: "#cbd5e0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem"
            }}>
              <div className="spinner-border" role="status" style={{ 
                width: "2.5rem", 
                height: "2.5rem",
                background: "conic-gradient(from 0deg, #1a1f36 0%, #2d3748, #667eea)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}>
                <span className="visually-hidden">{t('menu.loading')}</span>
              </div>
              <span style={{ fontSize: "0.875rem" }}>{t('menu.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État d'erreur
  if (error && (!role || role === "unknown")) {
    return (
      <div className="sidebar-modern">
        <style>{sidebarStyles}</style>
        <div className="dlabnav">
          <div className="dlabnav-scroll">
            <div style={{ 
              padding: "2rem", 
              textAlign: "center", 
              color: "#fc8181",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem"
            }}>
              <i className="material-icons" style={{ fontSize: "3rem" }}>error_outline</i>
              <div>
                <strong style={{ display: "block", marginBottom: "0.5rem" }}>{t('menu.error')}</strong>
                <div style={{ fontSize: "0.875rem", color: "#cbd5e0" }}>{error}</div>
              </div>
              <button 
                onClick={() => fetchUserRole(true)}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
                onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
              >
                <i className="material-icons" style={{ fontSize: "1rem" }}>refresh</i>
                {t('menu.retry')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar-modern">
      <style>{sidebarStyles}</style>
      <div className="dlabnav">
        {user && (
          <div className="dlabnav-scroll">
            {loading && role && (
              <div className="loading-indicator" style={{ 
                padding: "0.75rem", 
                textAlign: "center", 
                color: "#cbd5e0",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem"
              }}>
                <i className="material-icons" style={{ fontSize: "1rem" }}>sync</i>
                {t('menu.roleUpdate')}
              </div>
            )}
            
            <ul className="metismenu" id="menu">
              {menuItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path} style={{ animationDelay: `${index * 0.05 + 0.1}s` }}>
                    <Link
                      to={item.path}
                      className={isActive ? "active-link" : ""}
                      onMouseEnter={() => setHovered(item.path)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i className="material-icons">{item.icon}</i>
                      <span className="nav-text">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}